import React, { useState, useEffect } from 'react';
import { Modal, Card, Button, Form } from 'react-bootstrap';
import Select, { StylesConfig } from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Job, NewJobModalProps, JobStatus, createJobWithDefaults } from './Interfaces';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';
import JobStatusService from '../../utils/jobStatusService';

interface SelectOption {
  label: string;
  value: string;
}

interface CustomerData {
  id: string;
  name: string;
  company?: string;
  contacts?: Array<{
    name: string;
    email: string;
  }>;
  email?: string;
}

const NewJobModal: React.FC<NewJobModalProps> = ({ 
  show, 
  onClose, 
  initialCustomerId, 
  initialDescription,
  onJobCreated 
}) => {
  const [customers, setCustomers] = useState<SelectOption[]>([]);
  const [customerData, setCustomerData] = useState<CustomerData[]>([]);
  const [hasAppliedInitialValues, setHasAppliedInitialValues] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const fetchCustomers = async () => {
      const querySnapshot = await getDocs(collection(db, 'customers'));
      const fetchedCustomers: CustomerData[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CustomerData[];

      setCustomerData(fetchedCustomers);

      const customerOptions: SelectOption[] = fetchedCustomers.map(customer => ({
        label: `${customer.name}${customer.company ? ` - ${customer.company}` : ''}`,
        value: customer.id
      }));
      setCustomers(customerOptions);
    };
    fetchCustomers();
  }, []);

  // Apply initial values when modal opens with pre-selected customer/description
  useEffect(() => {
    if (show && !hasAppliedInitialValues && customerData.length > 0) {
      if (initialCustomerId) {
        formik.setFieldValue('customerId', [initialCustomerId]);
      }
      if (initialDescription) {
        formik.setFieldValue('description', initialDescription);
      }
      setHasAppliedInitialValues(true);
    }
  }, [show, hasAppliedInitialValues, customerData.length, initialCustomerId, initialDescription]);

  // Reset the flag when modal closes so it can be applied again next time
  useEffect(() => {
    if (!show) {
      setHasAppliedInitialValues(false);
    }
  }, [show]);

  const customSelectStyles: StylesConfig<SelectOption, true> = {
    control: (styles) => ({ 
      ...styles, 
      backgroundColor: 'var(--bs-secondary-bg)',
    }),
    menu: (styles) => ({ 
      ...styles, 
      backgroundColor: 'var(--bs-secondary-bg)',
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isFocused ? 'var(--bs-secondary-bg)' : isSelected ? 'var(--bs-secondary-bg)' : undefined,
      color: 'var(--bs-body-color)',
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--bs-secondary-bg)',
      },
    }),
    input: (styles) => ({
      ...styles,
      color: 'var(--bs-body-color)',
    }),
    singleValue: (styles) => ({
      ...styles,
      color: 'var(--bs-body-color)',
    }),
    placeholder: (styles) => ({
      ...styles,
      color: 'var(--bs-body-color)',
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: 'var(--bs-secondary-bg)',
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: 'var(--bs-body-color)',
    }),
  };

  const jobSchema = Yup.object().shape({
    creationDate: Yup.string().required('Creation Date is required'),
    customerId: Yup.array().of(Yup.string()).min(1, 'At least one customer is required').required('Customer is required'),
    customerName: Yup.array().of(Yup.string()),
    jobLabel: Yup.string().required('Job Label is required'),
    description: Yup.string().required('Description is required'),
    unbilledHours: Yup.number().required('Unbilled Hours are required').min(0),
    totalHours: Yup.number().required('Total Hours are required').min(0),
    dueDate: Yup.string().required('Due Date is required'),
    status: Yup.string().required('Status is required')
  });

  const initialValues: Job = createJobWithDefaults({
    id: '',
    creationDate: today,
    customerId: [],
    jobLabel: '',
    description: '',
    unbilledHours: 0,
    totalHours: 0,
    dueDate: '',
    status: 'Open' as JobStatus,
    lastEdited: today
  });

  const formik = useFormik({
    initialValues,
    validationSchema: jobSchema,
    onSubmit: async (values) => {
      try {
        const { id, ...jobData } = values;
        
        // Get company names for selected customers
        const selectedCustomers = customerData.filter(customer => 
          values.customerId.includes(customer.id)
        );
        
        // Create company prefix
        const companyPrefix = selectedCustomers
          .map(customer => customer.company || customer.name)
          .join(' & ');
        
        // Modify job label to include company prefix
        const fullJobLabel = `${companyPrefix} - ${jobData.jobLabel}`;
        const modifiedJobData = {
          ...jobData,
          jobLabel: fullJobLabel
        };

        const jobRef = doc(collection(db, 'jobs'));
        await setDoc(jobRef, modifiedJobData);
        
        // Call callback with full job object if provided
        if (onJobCreated) {
          const createdJob: Job = {
            ...modifiedJobData,
            id: jobRef.id
          } as Job;
          onJobCreated(createdJob);
        }
        
        formik.resetForm();
        onClose();
      } catch (error) {
        console.error('Error adding new job: ', error);
      }
    }
  });

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>New Job</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card>
          <Card.Body>
            <Form onSubmit={formik.handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="creationDate">Creation Date</Form.Label>
                <Form.Control type="date" id="creationDate" {...formik.getFieldProps('creationDate')} />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="jobLabel">Job Label</Form.Label>
                <Form.Control 
                  type="text" 
                  id="jobLabel" 
                  {...formik.getFieldProps('jobLabel')} 
                  isInvalid={formik.touched.jobLabel && !!formik.errors.jobLabel}
                />
                {formik.touched.jobLabel && formik.errors.jobLabel ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.jobLabel}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="customerId">Customer</Form.Label>
                <Select
                  options={customers}
                  isMulti
                  onChange={(selectedOptions) => {
                    const values = Array.isArray(selectedOptions)
                      ? selectedOptions.map(option => option.value)
                      : [];
                    formik.setFieldValue('customerId', values);
                  }}
                  styles={customSelectStyles}
                  placeholder="Select Customer(s)"
                  value={customers.filter(option => formik.values.customerId.includes(option.value))}
                  onBlur={() => formik.setFieldTouched('customerId', true)}
                  filterOption={(option, input) => {
                    if (!input) return true;
                    const customer = customerData.find(c => c.id === option.value);
                    if (!customer) return false;
                    
                    const searchTerm = input.toLowerCase();
                    const matchName = customer.name?.toLowerCase().includes(searchTerm);
                    const matchCompany = customer.company?.toLowerCase().includes(searchTerm);
                    const matchEmail = customer.email?.toLowerCase().includes(searchTerm);
                    const matchContacts = customer.contacts?.some(contact => 
                      contact.name?.toLowerCase().includes(searchTerm) ||
                      contact.email?.toLowerCase().includes(searchTerm)
                    );

                    return matchName || matchCompany || matchEmail || matchContacts || false;
                  }}
                />
                {formik.touched.customerId && formik.errors.customerId && (
                  <div className="text-danger">{formik.errors.customerId}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="description">Description</Form.Label>
                <Form.Control 
                  as="textarea" 
                  id="description" 
                  {...formik.getFieldProps('description')}
                  isInvalid={formik.touched.description && !!formik.errors.description}
                />
                {formik.touched.description && formik.errors.description ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.description}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="unbilledHours">Unbilled Hours</Form.Label>
                <Form.Control 
                  type="number" 
                  id="unbilledHours" 
                  {...formik.getFieldProps('unbilledHours')}
                  isInvalid={formik.touched.unbilledHours && !!formik.errors.unbilledHours}
                />
                {formik.touched.unbilledHours && formik.errors.unbilledHours ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.unbilledHours}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="totalHours">Total Hours</Form.Label>
                <Form.Control 
                  type="number" 
                  id="totalHours" 
                  {...formik.getFieldProps('totalHours')}
                  isInvalid={formik.touched.totalHours && !!formik.errors.totalHours}
                />
                {formik.touched.totalHours && formik.errors.totalHours ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.totalHours}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="status">Status</Form.Label>
                <Form.Select 
                  id="status" 
                  {...formik.getFieldProps('status')}
                  isInvalid={formik.touched.status && !!formik.errors.status}
                >
                  <option value="Pending">Pending Start</option>
                  <option value="Open">Ready to Start</option>
                  <option value="Awaiting Client Response">Awaiting Response</option>
                  <option value="Overdue">Overdue</option>
                  <option value="Closed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Archived">Archived</option>
                </Form.Select>
                {formik.touched.status && formik.errors.status ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.status}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="dueDate">Due Date</Form.Label>
                <Form.Control 
                  type="date" 
                  id="dueDate" 
                  {...formik.getFieldProps('dueDate')}
                  isInvalid={formik.touched.dueDate && !!formik.errors.dueDate}
                />
                {formik.touched.dueDate && formik.errors.dueDate ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.dueDate}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button 
                  type="submit" 
                  variant="primary" 
                  className="me-2"
                  disabled={!formik.isValid || !formik.dirty}
                >
                  Save Job
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => formik.resetForm()}
                >
                  Reset
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewJobModal;